import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import config from '../../../config';
import { logout } from '../../../ducks/auth.duck';
import { setOnlineStatus } from '../../../ducks/startUp.duck';
import { hasCurrentUserErrors } from '../../../ducks/user.duck';
import routeConfiguration from '../../../routeConfiguration';
import { yellow } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import useAppTheme from '../../../util/hooks/useAppTheme';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { subscribeNetworkTrouble } from '../../../util/networkStatus';
import { FormattedMessage } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';
import YellowBanner from '../../Banner/YellowBanner';
import LimitedAccessBanner from '../../LimitedAccessBanner';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

/**
 * @param {object} params
 * @param {boolean} params.show
 */
// How long the transient (timeout / network / gateway 5xx) banner stays up
// before auto-dismissing. Device-offline stays persistent via `isOffline`.
const TRANSIENT_ERROR_MS = 6000;

const GenericError = ({ show }) => {
  const dispatch = useDispatch();
  const theme = useAppTheme();
  const { isMobile } = useScreenSize();
  // Device connectivity (navigator-based) — persistent while offline.
  const [isOffline, setIsOffline] = useState(false);
  // A request hit transient trouble (timeout / network / gateway 5xx) — shown
  // briefly, then auto-dismissed.
  const [troubleActive, setTroubleActive] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkOnlineStatus = () => {
      const isReallyOffline =
        typeof navigator.connection?.type === 'string'
          ? navigator.connection.type === 'none'
          : !navigator.onLine;

      dispatch(setOnlineStatus(!isReallyOffline));
      if (isMounted) setIsOffline(isReallyOffline);
    };

    checkOnlineStatus();

    let lastStatus = navigator.onLine;
    const poll = setInterval(() => {
      if (navigator.onLine !== lastStatus) {
        lastStatus = navigator.onLine;
        checkOnlineStatus();
      }
    }, 2000);
    return () => {
      isMounted = false;
      clearInterval(poll);
    };
  }, [dispatch]);

  // Surface the generic banner when a backend request times out / fails at the
  // network level / returns a gateway 5xx (api.js emits via networkStatus).
  useEffect(() => {
    let timer = null;
    const unsubscribe = subscribeNetworkTrouble(() => {
      setTroubleActive(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setTroubleActive(false), TRANSIENT_ERROR_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const showError = show || isOffline || troubleActive;

  const styles = useMemo(
    () => ({
      position: 'fixed',
      top: !isMobile ? `calc(${theme.globals.topBarHeightDesktop}px + 44px + 15px)` : '10px',
      right: showError ? 0 : '-400px',
      opacity: showError ? 1 : 0,
      maxWidth: '400px',
      margin: '0 15px',
      /* Animate error from the right */
      transition: 'all ease-out 0.2s',
      transitionDelay: '1s',
      /* Bleed the bg color from the bottom */
      paddingBottom: '4px',
      backgroundColor: theme.palette.info.main,
      borderRadius: '4px',
      /* Place on top of everything */
      zIndex: theme.globals.zIndexGenericError,
    }),
    [isMobile, showError], // eslint-disable-line
  );

  return showError ? (
    <Box sx={styles}>
      <Box
        sx={{
          padding: '16px 22px',
          borderRadius: '4px',
          backgroundColor: yellow.main,
        }}
      >
        <Typography color={theme.palette.common.white} fontWeight="bold" margin={0}>
          <FormattedMessage id="Topbar.genericError" />
        </Typography>
      </Box>
    </Box>
  ) : null;
};

/**
 * @param {object} params
 * @param {object} [] - search params from the URL
 * @param {string} [params.currentPage] - name of the current page
 * @returns {React.JSX.Element | null}
 */

const TobBarComponent = ({ currentPage = undefined }) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch();
  const theme = useAppTheme();
  const { isMobile, isTablet } = useScreenSize();

  const { isAuthenticated, logoutError, authScopes } = useAppSelector(
    (state) => ({
      isAuthenticated: state.auth.isAuthenticated,
      logoutError: state.auth.logoutError,
      authScopes: state.auth.authScopes,
    }),
    shallowEqual,
  );

  const hasGenericError = useAppSelector((state) => !!(logoutError || hasCurrentUserErrors(state) || state.startUp.onlineStatus === false));

  const handleLogout = () => {
    dispatch(logout()).then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration(), {}, intl.locale);
      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (config.dev) {
        navigate(path);
      } else if (typeof window !== 'undefined') {
        window.routerNavigate(path, { replace: true });
      }
      console.log('logged out');  
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: 1,
        zIndex: theme.globals.zIndexTopBar,
      }}
    >
      {(isMobile || isCordova()) ? null : (<>
          <LimitedAccessBanner
            authScopes={authScopes}
            currentPage={currentPage}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
          />
          <YellowBanner currentPage={currentPage} />
          <TopbarDesktop
            currentPage={currentPage}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
          />
        </>
      )}
      <GenericError show={hasGenericError} />
    </Box>
  );
};

TobBarComponent.displayName = 'TopBar';

const TopBar = memo(TobBarComponent);

export default TopBar;
